import React, { useEffect, useRef, useState } from "react";
import CalendarDateInput from "./CalendarDateInput";
import { DATE_FORMAT_MESSAGE } from "../../../utils/fieldMessages";

export type DateRangeFilterValue = { from: string; to: string };
export type DateRangeFilterFieldProps = DateRangeFilterValue & {
  onChange: (range: DateRangeFilterValue) => void;
  label?: string;
  title?: string;
  disabled?: boolean;
};

const formatDate = (value: string) => {
  const date = value ? new Date(`${value}T12:00:00`) : null;
  return date && !Number.isNaN(date.getTime())
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date)
    : "";
};

const isValidCalendarDate = (value: string) => {
  if (!value) return true;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

export type DateRangeFilterPanelProps = DateRangeFilterValue & {
  // Receives only a valid range (each date empty or complete, To not before
  // From). Partial typed text stays in the panel with an error.
  onChange: (range: DateRangeFilterValue) => void;
  onDone: () => void;
  // "change" applies each valid edit at once (design DateRangeFilter.jsx
  // set()); "done" applies the range only on Done.
  applyOn?: "change" | "done";
  title: string;
  idPrefix: string;
  doneRef?: React.Ref<HTMLButtonElement>;
};

// Claude Design ds-components/DateRangeFilter.jsx popover body: title, hint,
// From/To fields with an inline Monday-first calendar, Clear dates and Done.
// Shared by the Renewal Date filter (Manage Members), the Invoice Date Range
// (Manage Invoices) and the Event Date Range (Manage Events).
export const DateRangeFilterPanel = ({
  from,
  to,
  onChange,
  onDone,
  applyOn = "change",
  title,
  idPrefix,
  doneRef,
}: DateRangeFilterPanelProps) => {
  const [calendarContainer, setCalendarContainer] = useState<HTMLDivElement | null>(null);
  const [draft, setDraft] = useState<DateRangeFilterValue>({ from, to });
  // An outside change (for example Clear all filters) replaces the draft.
  useEffect(() => {
    setDraft({ from, to });
  }, [from, to]);
  const error = getDateRangeFilterError(draft);
  const updateDraft = (next: DateRangeFilterValue) => {
    setDraft(next);
    if (applyOn === "change" && !getDateRangeFilterError(next)) onChange(next);
  };
  return (
    <div className="flex flex-col gap-[8px] leading-[normal] text-[#0A0A0A]">
      <h3 className="text-[16px] font-semibold">{title}</h3>
      <p className="mb-[8px] text-[14px] leading-[normal] text-[#0A0A0A]">
        Select one date or a from/to range.
      </p>
      <div className="grid grid-cols-2 gap-[12px]">
        {(["from", "to"] as const).map((bound) => (
          <div key={bound} className="min-w-0">
            <label
              htmlFor={`${idPrefix}-${bound}`}
              className="mb-[8px] block text-[15px] font-semibold"
            >
              {bound === "from" ? "From" : "To"}
            </label>
            <CalendarDateInput
              id={`${idPrefix}-${bound}`}
              ariaLabel={`${title} ${bound}`}
              calendarVariant="range"
              calendarContainer={calendarContainer}
              displayFormat="long"
              mode="fullDate"
              value={draft[bound]}
              onChange={(value) => updateDraft({ ...draft, [bound]: value })}
              placeholder="Select Date"
              inputClassName="w-full truncate rounded-[8px] border border-[#ECECEC] bg-white py-[12px] pl-[14px] pr-[38px] text-[14px] leading-[normal] text-[#0A0A0A] outline-none focus:border-[#E1A731]"
            />
          </div>
        ))}
      </div>
      <div ref={setCalendarContainer} className="empty:hidden" />
      {error && (
        <p role="alert" className="mt-[8px] text-[14px] text-[#E24C4C]">
          {error}
        </p>
      )}
      <div className="mt-[18px] flex items-center justify-end gap-[16px]">
        <button
          type="button"
          className="px-[4px] py-[8px] text-[15px] text-[#0A0A0A]"
          onClick={() => {
            setDraft({ from: "", to: "" });
            onChange({ from: "", to: "" });
          }}
        >
          Clear dates
        </button>
        <button
          ref={doneRef}
          type="button"
          disabled={Boolean(error)}
          onClick={() => {
            if (applyOn === "done") onChange(draft);
            onDone();
          }}
          className="rounded-[8px] bg-[#E1A731] px-[30px] py-[12px] text-[15px] font-bold text-white hover:bg-[#C6912A] disabled:opacity-50"
        >
          Done
        </button>
      </div>
    </div>
  );
};

// Shared range check: each date is empty or a real date, and To is not
// before From.
export const getDateRangeFilterError = ({ from, to }: DateRangeFilterValue) =>
  !isValidCalendarDate(from) || !isValidCalendarDate(to)
    ? `${DATE_FORMAT_MESSAGE} Or choose a date from the calendar.`
    : from && to && from > to
      ? "The To date must be on or after the From date."
      : "";

export const DateRangeFilterField = ({
  from,
  to,
  onChange,
  label = "Filter by Renewal Date",
  title = "Renewal Date Range",
  disabled = false,
}: DateRangeFilterFieldProps) => {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const doneRef = useRef<HTMLButtonElement>(null);
  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };
  useEffect(() => {
    if (!open) return;
    const ownedCalendarPanels = () =>
      Array.from(
        rootRef.current?.querySelectorAll<HTMLInputElement>(
          'input[aria-expanded="true"][aria-haspopup="dialog"][aria-controls]',
        ) || [],
      ).flatMap((input) =>
        (input.getAttribute("aria-controls") || "")
          .split(/\s+/)
          .map((id) => document.getElementById(id))
          .filter((panel): panel is HTMLElement => panel !== null),
      );
    const outside = (event: MouseEvent) => {
      const target = event.target;
      if (
        target instanceof Node &&
        !rootRef.current?.contains(target) &&
        !ownedCalendarPanels().some((panel) => panel.contains(target))
      ) {
        setOpen(false);
      }
    };
    const keyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        // The nested calendar handles the first Escape. Its input identifies
        // the owned portal, so unrelated dialogs do not keep this range open.
        if (ownedCalendarPanels().length > 0) return;
        event.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", keyDown);
    doneRef.current?.focus();
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", keyDown);
    };
  }, [open]);
  const summary =
    from && to
      ? `${formatDate(from)} – ${formatDate(to)}`
      : formatDate(from || to);
  return (
    <div ref={rootRef} className="relative min-w-0">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={summary || label}
        onClick={() => {
          if (open) close();
          else {
            const bounds = buttonRef.current?.getBoundingClientRect();
            setAlignRight(Boolean(bounds && bounds.left + 420 > window.innerWidth - 12));
            setOpen(true);
          }
        }}
        className={`flex w-full items-center gap-[10px] rounded-[8px] border bg-white px-[16px] py-[12px] text-left text-[14px] leading-[normal] disabled:opacity-50 ${summary ? "border-[#E1A731] text-[#0A0A0A]" : "border-[#9E9E9E] text-[#727272]"}`}
      >
        <svg
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#727272"
          strokeWidth="1.7"
          aria-hidden="true"
          className="shrink-0"
        >
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M7 3v4M17 3v4M3 10h18" />
        </svg>
        <span className="min-w-0 flex-1 truncate font-normal">{summary || label}</span>
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={title}
          className={`absolute top-full z-[60] mt-[8px] w-[420px] max-w-[92vw] rounded-[12px] border border-[#ECECEC] bg-white p-[22px] shadow-[0_12px_32px_rgba(0,0,0,.14)] ${alignRight ? "right-0" : "left-0"}`}
        >
          <DateRangeFilterPanel
            from={from}
            to={to}
            onChange={onChange}
            onDone={close}
            applyOn="done"
            title={title}
            idPrefix="renewal-date"
            doneRef={doneRef}
          />
        </div>
      )}
    </div>
  );
};

export default DateRangeFilterField;
